import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Search, TrendingDown, Store, Calendar, AlertCircle, Loader2 } from 'lucide-react';
import { itemsApi } from '../api/items';
import type { PricePoint } from '../api/items';

// =============================================
// Konstanten
// =============================================
const CHART_COLORS = [
  '#3B82F6',
  '#8B5CF6',
  '#F59E0B',
  '#22C55E',
  '#EF4444',
  '#06B6D4',
  '#EC4899',
];

// =============================================
// Hilfsfunktionen
// =============================================
function formatPrice(value: number): string {
  return value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatDateShort(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
  });
}

// =============================================
// Daten-Transformation: PricePoints -> Chart-Daten
// =============================================
interface ChartDataPoint {
  date: string;
  rawDate: string;
  [store: string]: number | string;
}

function buildChartData(prices: PricePoint[]): { data: ChartDataPoint[]; stores: string[] } {
  const storeSet = new Set<string>();
  prices.forEach((p) => storeSet.add(p.store_name));
  const stores = Array.from(storeSet);

  // Gruppierung nach Datum, dann nach Store
  const byDate = new Map<string, ChartDataPoint>();
  prices.forEach((p) => {
    const key = p.receipt_date.substring(0, 10);
    if (!byDate.has(key)) {
      byDate.set(key, { date: formatDateShort(p.receipt_date), rawDate: key });
    }
    const point = byDate.get(key)!;
    point[p.store_name] = p.unit_price;
  });

  const data = Array.from(byDate.values()).sort((a, b) =>
    a.rawDate.localeCompare(b.rawDate)
  );

  return { data, stores };
}

// =============================================
// Custom Tooltip
// =============================================
interface TooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}

const CustomTooltip: React.FC<TooltipProps> = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-lg p-3 text-sm">
      <p className="font-semibold text-neutral-700 dark:text-neutral-200 mb-2">{label}</p>
      {payload.map((entry) => (
        <div key={entry.name} className="flex items-center gap-2 mb-1">
          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
          <span className="text-neutral-500 dark:text-neutral-400">{entry.name}:</span>
          <span className="font-medium text-neutral-800 dark:text-neutral-100">{formatPrice(entry.value)}</span>
        </div>
      ))}
    </div>
  );
};

// =============================================
// PriceComparisonPage
// =============================================
export const PriceComparisonPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const [searchTerm, setSearchTerm] = useState(initialQuery);
  const [debouncedSearch, setDebouncedSearch] = useState(initialQuery);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(initialQuery.length >= 2);
  const searchRef = useRef<HTMLDivElement>(null);

  // Debounce: 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Click-outside: Vorschläge schliessen
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Suchanfragen-Query
  const {
    data: suggestions,
    isFetching: isFetchingSuggestions,
  } = useQuery({
    queryKey: ['items', 'search', debouncedSearch],
    queryFn: () => itemsApi.search(debouncedSearch),
    enabled: debouncedSearch.length >= 2,
    staleTime: 30_000,
  });

  // Preisverlauf-Query
  const {
    data: priceComparison,
    isFetching: isFetchingPrices,
    isError: isPriceError,
  } = useQuery({
    queryKey: ['items', 'price-comparison', selectedItem],
    queryFn: () => itemsApi.getPriceComparison(selectedItem!),
    enabled: selectedItem !== null,
    staleTime: 60_000,
  });

  // Günstigste Artikel Query
  const {
    data: cheapestItems,
    isLoading: isLoadingCheapest,
    isError: isCheapestError,
  } = useQuery({
    queryKey: ['items', 'cheapest'],
    queryFn: () => itemsApi.getCheapestItems(20),
    staleTime: 120_000,
  });

  // Artikel auswählen
  const handleSelectSuggestion = (name: string) => {
    setSelectedItem(name);
    setSearchTerm(name);
    setShowSuggestions(false);
  };

  // Chart-Daten vorbereiten
  const chartInfo =
    priceComparison && priceComparison.prices.length > 0
      ? buildChartData(priceComparison.prices)
      : null;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-24 lg:pb-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-800 dark:text-neutral-100">Preisvergleich</h1>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
          Vergleiche Preise eines Artikels quer durch alle Stores
        </p>
      </div>

      {/* Suchfeld */}
      <div ref={searchRef} className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setShowSuggestions(true);
              if (e.target.value === '') setSelectedItem(null);
            }}
            onFocus={() => setShowSuggestions(true)}
            placeholder="Artikel suchen, z. B. Milch, Butter..."
            className="w-full pl-10 pr-10 py-3 rounded-2xl border border-neutral-200 dark:border-neutral-700
                       bg-white dark:bg-neutral-800 text-neutral-800 dark:text-neutral-100
                       placeholder-neutral-400 text-sm
                       focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent
                       transition-all duration-200"
            aria-label="Artikel suchen"
            aria-autocomplete="list"
            aria-expanded={showSuggestions}
          />
          {isFetchingSuggestions && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 animate-spin" />
          )}
        </div>

        {/* Dropdown Vorschläge */}
        {showSuggestions && suggestions && suggestions.length > 0 && (
          <ul
            role="listbox"
            className="absolute z-20 left-0 right-0 mt-1 bg-white dark:bg-neutral-800
                       border border-neutral-200 dark:border-neutral-700 rounded-2xl
                       shadow-lg overflow-hidden max-h-60 overflow-y-auto"
          >
            {suggestions.map((suggestion) => (
              <li key={suggestion.name} role="option" aria-selected={selectedItem === suggestion.name}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelectSuggestion(suggestion.name);
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm
                             flex items-center justify-between
                             hover:bg-neutral-50 dark:hover:bg-neutral-700
                             transition-colors duration-150"
                >
                  <span className="text-neutral-700 dark:text-neutral-200 font-medium">
                    {suggestion.name}
                  </span>
                  <span className="text-neutral-400 dark:text-neutral-500 text-xs">
                    {suggestion.count} {suggestion.count === 1 ? 'Beleg' : 'Belege'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {showSuggestions && debouncedSearch.length >= 2 && !isFetchingSuggestions && suggestions?.length === 0 && (
          <div className="absolute z-20 left-0 right-0 mt-1 bg-white dark:bg-neutral-800
                          border border-neutral-200 dark:border-neutral-700 rounded-2xl
                          shadow-lg px-4 py-3 text-sm text-neutral-400 dark:text-neutral-500">
            Keine Artikel gefunden.
          </div>
        )}
      </div>

      {/* Preisverlauf-Chart */}
      {selectedItem && (
        <div className="bg-white dark:bg-neutral-800 rounded-2xl border border-neutral-200 dark:border-neutral-700 p-4 shadow-sm">
          <h2 className="text-base font-semibold text-neutral-700 dark:text-neutral-200 mb-1">
            Preisverlauf: <span className="text-primary-600 dark:text-primary-400">{selectedItem}</span>
          </h2>
          <p className="text-xs text-neutral-400 dark:text-neutral-500 mb-4">
            Preis in EUR pro Store im Zeitverlauf
          </p>

          {isFetchingPrices && (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
            </div>
          )}

          {isPriceError && !isFetchingPrices && (
            <div className="flex items-center gap-2 text-red-500 bg-red-50 dark:bg-red-900/20 rounded-xl p-3">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">Preisdaten konnten nicht geladen werden.</span>
            </div>
          )}

          {!isFetchingPrices && !isPriceError && priceComparison && chartInfo === null && (
            <div className="text-center text-sm text-neutral-400 dark:text-neutral-500 py-8">
              Noch keine Preisdaten fuer diesen Artikel vorhanden.
            </div>
          )}

          {!isFetchingPrices && !isPriceError && chartInfo && (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={chartInfo.data} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: '#9CA3AF' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={(v: number) => `${v.toFixed(2)} €`}
                  tick={{ fontSize: 11, fill: '#9CA3AF' }}
                  axisLine={false}
                  tickLine={false}
                  width={64}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: '12px', paddingTop: '12px' }}
                />
                {chartInfo.stores.map((store, idx) => (
                  <Line
                    key={store}
                    type="monotone"
                    dataKey={store}
                    name={store}
                    stroke={CHART_COLORS[idx % CHART_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 3, strokeWidth: 2 }}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      {/* Günstigste Artikel */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <TrendingDown className="w-5 h-5 text-green-500" />
          <h2 className="text-base font-semibold text-neutral-700 dark:text-neutral-200">
            Guenstigste Artikel
          </h2>
        </div>

        {isLoadingCheapest && (
          <div className="flex items-center justify-center h-24">
            <Loader2 className="w-5 h-5 text-primary-500 animate-spin" />
          </div>
        )}

        {isCheapestError && !isLoadingCheapest && (
          <div className="flex items-center gap-2 text-red-500 bg-red-50 dark:bg-red-900/20 rounded-xl p-3">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm">Artikel konnten nicht geladen werden.</span>
          </div>
        )}

        {!isLoadingCheapest && !isCheapestError && cheapestItems && cheapestItems.length === 0 && (
          <div className="text-center text-sm text-neutral-400 dark:text-neutral-500 py-8">
            Noch keine Artikel erfasst.
          </div>
        )}

        {!isLoadingCheapest && !isCheapestError && cheapestItems && cheapestItems.length > 0 && (
          <div className="space-y-2">
            {cheapestItems.map((item, idx) => (
              <div
                key={`${item.item_name}-${idx}`}
                className="bg-white dark:bg-neutral-800 rounded-2xl border border-neutral-200 dark:border-neutral-700
                           p-3.5 shadow-sm flex items-center gap-3
                           hover:border-primary-200 dark:hover:border-primary-700
                           transition-colors duration-200 cursor-pointer"
                role="button"
                tabIndex={0}
                aria-label={`${item.item_name} fuer ${formatPrice(item.unit_price)} bei ${item.cheapest_store}`}
                onClick={() => handleSelectSuggestion(item.item_name)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleSelectSuggestion(item.item_name);
                }}
              >
                {/* Rang-Badge */}
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0
                    ${idx === 0 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                      idx === 1 ? 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300' :
                      idx === 2 ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                      'bg-neutral-50 text-neutral-400 dark:bg-neutral-700/50 dark:text-neutral-500'}`}
                >
                  {idx + 1}
                </div>

                {/* Artikelname + Store */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 truncate">
                    {item.item_name}
                  </p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Store className="w-3 h-3 text-neutral-400 flex-shrink-0" />
                    <span className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                      {item.cheapest_store}
                    </span>
                    <span className="text-neutral-300 dark:text-neutral-600 text-xs">·</span>
                    <Calendar className="w-3 h-3 text-neutral-400 flex-shrink-0" />
                    <span className="text-xs text-neutral-400 dark:text-neutral-500">
                      {formatDate(item.last_seen)}
                    </span>
                  </div>
                </div>

                {/* Preis */}
                <div className="text-right flex-shrink-0">
                  <span className="text-sm font-bold text-green-600 dark:text-green-400">
                    {formatPrice(item.unit_price)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
